import React, { useEffect, useState, useRef } from 'react';

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import Login from '../Login';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { useUI } from '../UIContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import AbcNotation from '../components/AbcNotation';
import { useActiveSession } from '../ActiveSessionContext';
import { sanitizeAbcBody } from '../components/sanitizeAbcBody';
import { loadTuneHybrid } from '../utils/loadTuneHybrid';
import dexie from '../offlineDB';
import TuneRecorder from '../components/TuneRecorder';
import { deleteObject, ref as storageRef } from 'firebase/storage';
import abcjs from 'abcjs';
import { flattenAbcForPlayback } from '../utils/flattenAbcForPlayback';


export default function TuneDetailPage({ tunes }) {
  const { user } = useAuth();
  const { textStyle } = useUI();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loadedTune, setLoadedTune] = useState(null);
  const [loading, setLoading] = useState(false);
  const { activeSession } = useActiveSession();

  const fallbackTune = tunes?.find(t => t.id === id);
  const locationState = useRef(location.state || {}); // Persist location.state across re-renders
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const { uiColor, uiSize } = useUI();
const [validSessions, setValidSessions] = useState([]);


function AudioPlayerWithSpeed({ src }) {
  const [rate, setRate] = useState(1.0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  }, [rate]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
      <audio
        ref={audioRef}
        controls
        src={src}
        
        onLoadedMetadata={() => {
          if (audioRef.current) {
            audioRef.current.playbackRate = rate;
          }
        }}
      />
      <label style={{ fontSize: '0.9rem' }}>
        Speed:
        <select
          value={rate}
          onChange={(e) => setRate(parseFloat(e.target.value))}
          style={{ marginLeft: '6px' }}
        >
          {[0.5, 0.75, 1.0, 1.25, 1.5].map((r) => (
            <option key={r} value={r}>
              {r}×
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function AbcMidiPlayer({ abc }) {
  const divRef = useRef(null);
  const synthRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);

  const BASE_TEMPO = 140;

  const stop = () => {
    if (synthRef.current) {
      synthRef.current.stop();
      synthRef.current = null;
    }
    setIsPlaying(false);
  };

  const injectTempo = (abc, scaledTempo) => {
    const lines = abc.split('\n');
    const updated = [];
    let inserted = false;

    for (const line of lines) {
      if (line.startsWith('Q:')) {
        updated.push(`Q:1/4=${scaledTempo}`);
        inserted = true;
      } else if (line.match(/^[A-Z]:/)) {
        updated.push(line);
      } else if (!inserted && line.trim() !== '') {
        updated.push(`Q:1/4=${scaledTempo}`);
        updated.push(line);
        inserted = true;
      } else {
        updated.push(line);
      }
    }

    if (!inserted) {
      updated.unshift(`Q:1/4=${scaledTempo}`);
    }

    return updated.join('\n');
  };

  const playWithSpeed = async (speedValue) => {
    stop();

    if (!abcjs.synth.supportsAudio()) {
      alert("Audio not supported in this browser.");
      return;
    }

    try {
    const abcFlat = flattenAbcForPlayback(abc);
    const abcWithTempo = injectTempo(abcFlat, Math.round(BASE_TEMPO * speedValue));
      const visualObj = abcjs.renderAbc(divRef.current, abcWithTempo)[0];

      const synth = new abcjs.synth.CreateSynth();
      synthRef.current = synth;

      await synth.init({ visualObj });
      await synth.prime();
      synth.start();
      setIsPlaying(true);
    } catch (err) {
      console.error("ABC synth error:", err);
    }
  };

  const handleSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    if (isPlaying) {
      playWithSpeed(newSpeed);
    }
  };

  const handlePlay = () => {
    if (!isPlaying) {
      playWithSpeed(speed);
    }
  };

  return (
    <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
      <button
        onClick={handlePlay}
        disabled={isPlaying}
        style={{
          padding: '6px 16px',
          fontSize: '1rem',
          backgroundColor: isPlaying ? '#aaa' : '#444',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: isPlaying ? 'not-allowed' : 'pointer'
        }}
      >
        ▶️ Play ABC
      </button>
      <button
        onClick={stop}
        disabled={!isPlaying}
        style={{
          padding: '6px 12px',
          marginLeft: '8px',
          fontSize: '1rem',
          backgroundColor: '#e44',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: !isPlaying ? 'not-allowed' : 'pointer'
        }}
      >
        ⏹ Stop
      </button>
      <label style={{ marginLeft: '16px', fontSize: '0.9rem' }}>
        Speed:
        <select
          value={speed}
          onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
          style={{ marginLeft: '6px' }}
        >
          {[0.5, 0.75, 1.0, 1.25, 1.5].map((r) => (
            <option key={r} value={r}>{r}×</option>
          ))}
        </select>
      </label>
      <div ref={divRef} style={{ display: 'none' }} />
    </div>
  );
}










  useEffect(() => {
  if (location.state) {
    locationState.current = location.state;
  }
}, [location.state]);

  const tune = loadedTune || locationState.current.tune || fallbackTune;
const [currentTune, setCurrentTune] = useState(tune);

// Ensure attachment types are usable even from location.state fallback
if (tune?.attachments?.length) {
  tune.attachments = tune.attachments.map(file => ({
    ...file,
    type:
      file.type ||
      (file.name?.endsWith('.pdf') ? 'application/pdf'
        : /\.(jpg|jpeg|png|gif)$/i.test(file.name) ? 'image/jpeg'
        : 'application/octet-stream')
  }));
}


  const setTunes = locationState.current.setTunes || [];
  const setTitle = locationState.current.setTitle || null;
  const setId = locationState.current.setId || null;
  const currentIndex = setTunes.findIndex(t => t.id === id);
  const sessionTunes = locationState.current.sessionTunes || [];
  const sessionTitle = locationState.current.sessionTitle || null;
  const sessionIndex = sessionTunes.findIndex(t => t.id === id);
  const [fullscreenPdfUrl, setFullscreenPdfUrl] = useState(null);
  const [fullscreenImgUrl, setFullscreenImgUrl] = useState(null);


const tuneIds = location.state?.searchTuneIds || [];
const index = location.state?.currentIndex || 0;

const prevId = index > 0 ? tuneIds[index - 1] : null;
const nextId = index < tuneIds.length - 1 ? tuneIds[index + 1] : null;
const searchTuneIds = locationState.current.searchTuneIds || [];
const searchIndex = locationState.current.currentIndex ?? -1;



useEffect(() => {
  const fetchTune = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await loadTuneHybrid(user, id);
      if (data) setLoadedTune(data);
    } catch (err) {
      console.error('Error loading tune (hybrid):', err);
    } finally {
      setLoading(false);
    }
  };
  fetchTune();
}, [user, id]);

useEffect(() => {
  setCurrentTune(tune);
}, [tune]);


useEffect(() => {
  const filterValidSets = async () => {
    if (!user || !tune?.sets?.length) return;

    const validSets = [];

    for (const s of tune.sets) {
      const id = s.setId;
      if (!id) continue;

      let exists = false;
      if (navigator.onLine) {
        const snap = await getDoc(doc(db, 'users', user.uid, 'sets', id));
        exists = snap.exists();
      } else {
        const local = await dexie.sets.get(id);
        exists = !!local;
      }

      if (exists) validSets.push(s);
    }

    setLoadedTune(prev => ({
      ...prev,
      sets: validSets
    }));
  };

  filterValidSets();
}, [user, tune?.sets]);

useEffect(() => {
  if (!user || !tune?.sessions) return;

  const loadSessions = async () => {
    const checks = await Promise.all(
      tune.sessions.map(async (s) => {
        if (!s.sessionId) return null;

        let exists = false;
        if (navigator.onLine) {
          const snap = await getDoc(doc(db, 'users', user.uid, 'sessions', s.sessionId));
          exists = snap.exists();
        } else {
          const local = await dexie.sessions.get(s.sessionId);
          exists = !!local;
        }

        return exists ? s : null;
      })
    );
    setValidSessions(checks.filter(Boolean));
  };

  loadSessions();
}, [user, tune]);





  if (!user) return <PageWrapperAuto><Login /></PageWrapperAuto>;
if (loading) return <PageWrapperAuto><p>Loading tune...</p></PageWrapperAuto>;

if (!tune) {
  return (
    <PageWrapperAuto>
      <p style={{ textAlign: 'center' }}>
        ⚠️ Could not load tune. You may be offline, not logged in, or the tune was deleted.
      </p>
    </PageWrapperAuto>
  );
}

  return (
    <PageWrapperAuto>

      {setTitle && (
  <div style={{ marginTop: '12px', marginBottom: '12px', textAlign: 'center' }}>
    <h2 style={{ ...textStyle, textTransform: 'uppercase', marginBottom: '8px' }}>
      Set: {setTitle}
    </h2>
  </div>
)}

{setTunes.length > 1 && currentIndex !== -1 && (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '0 12px'
  }}>
    {currentIndex > 0 ? (
      <button
        className="big-button"
        onClick={() => {
          const prevItem = setTunes[currentIndex - 1];
          if (prevItem?.type === 'set') {
            navigate(`/sets/${prevItem.id}`, {
              state: {
                setId,
                setTitle,
                setTunes,
              }
            });
          }
          else {
            navigate(`/tune/${prevItem.id}`, {
              state: {
                tune: prevItem,
                setId,
                setTitle,
                setTunes
              }
            });
          }
        }}
      >
        ← Prev
      </button>

    ) : <div style={{ width: '80px' }}></div>}

    <span className="text-scaled" style={{ color: 'var(--text-muted)' }}>
      {currentIndex + 1} of {setTunes.length}
    </span>

    {currentIndex < setTunes.length - 1 ? (
      <button
        className="big-button"
        onClick={() => {
          const nextItem = setTunes[currentIndex + 1];
         if (nextItem?.type === 'set') {
          navigate(`/sets/${nextItem.id}`, {
            state: {
              setId,
              setTitle,
              setTunes,
            }
          });
        }
      else {
            navigate(`/tune/${nextItem.id}`, {
              state: {
                tune: nextItem,
                setId,
                setTitle,
                setTunes
              }
            });
          }
        }}
      >
        Next →
      </button>

    ) : <div style={{ width: '80px' }}></div>}
  </div>
)}

{sessionTitle && (
  <div style={{ marginTop: '12px', marginBottom: '12px', textAlign: 'center' }}>
    <h2 style={{ ...textStyle, textTransform: 'uppercase', marginBottom: '8px' }}>
      Session: {sessionTitle}
    </h2>
  </div>
)}

{sessionTunes.length > 1 && sessionIndex !== -1 && (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '0 12px'
  }}>
    {sessionIndex > 0 ? (
      <button className="big-button" onClick={() =>
        navigate(`/tune/${sessionTunes[sessionIndex - 1].id}`, {
          state: { tune: sessionTunes[sessionIndex - 1], sessionTunes, sessionTitle }
        })}>
        ← Prev
      </button>
    ) : <div style={{ width: '80px' }}></div>}

    <span className="text-scaled" style={{ color: 'var(--text-muted)' }}>
      {sessionIndex + 1} of {sessionTunes.length}
    </span>

    {sessionIndex < sessionTunes.length - 1 ? (
      <button className="big-button" onClick={() =>
        navigate(`/tune/${sessionTunes[sessionIndex + 1].id}`, {
          state: { tune: sessionTunes[sessionIndex + 1], sessionTunes, sessionTitle }
        })}>
        Next →
      </button>
    ) : <div style={{ width: '80px' }}></div>}
  </div>
)}

{searchTuneIds.length > 1 && searchIndex !== -1 && (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '0 12px'
  }}>
    {searchIndex > 0 ? (
      <button className="big-button" onClick={() =>
        navigate(`/tune/${searchTuneIds[searchIndex - 1]}`, {
          state: {
            searchTuneIds,
            currentIndex: searchIndex - 1
          }
        })}>
        ← Prev
      </button>
    ) : <div style={{ width: '80px' }}></div>}

    <div style={{ width: '80px' }}></div> {/* Spacer instead of counter */}

    {searchIndex < searchTuneIds.length - 1 ? (
      <button className="big-button" onClick={() =>
        navigate(`/tune/${searchTuneIds[searchIndex + 1]}`, {
          state: {
            searchTuneIds,
            currentIndex: searchIndex + 1
          }
        })}>
        Next →
      </button>
    ) : <div style={{ width: '80px' }}></div>}
  </div>
)}


<div
  className="page"
  style={{
    marginTop: '0px',
    paddingTop: '10px',
    maxWidth: '95vw',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  }}
>

        <div
          className="card"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--card-border)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '20px'
          }}
        >  
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
  <button
    className="big-button"
    onClick={() => navigate(-1)}
    style={{
      background: '#444',
      color: '#fff',
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem'
    }}
  >
    ← Back
  </button>

  <button
    className="big-button"
    onClick={() => navigate(`/edit/${tune.id || "imported"}`, { state: { tune } })}

        style={{
      background: '#ccc',
      color: '#000',
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem'
    }}
  >
    ✏️ Edit Tune
  </button>
</div>
<div style={{ textAlign: 'center' }}>

      
          <h2>{tune.title}</h2>

          <p><strong>Genre:</strong> {tune.genre}</p>

          <p><strong>Type:</strong> {tune.type}</p>

          {tune.key && <p><strong>Key:</strong> {tune.key}</p>}

          {tune.composer && <p><strong>Composer:</strong> {tune.composer}</p>}

          {tune.status && tune.status.trim() !== '' && tune.status !== '—' && tune.status !== '-' && (
            <p><strong>Status:</strong> {tune.status}</p>
          )}

          {tune.rating && tune.rating !== '—' && <p><strong>Rating:</strong> {tune.rating}</p>}


          {Array.isArray(tune.customTags) && tune.customTags.length > 0 && (
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <strong>Tags:</strong>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '6px',
                  marginTop: '6px',
                  maxWidth: '420px', // 💡 approx. 6 tag widths
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}
              >
                {tune.customTags.map((tag, i) => (
  <span
    key={i}
   onClick={() => {
  let hasFilters = false;

  try {
    const stored = JSON.parse(localStorage.getItem('activeSearchFilters') || '{}');

    const isActive = (val) => val !== null && val !== undefined && val !== '' && val !== '—';

    hasFilters =
      isActive(stored.searchQuery) ||
      isActive(stored.filterKey) ||
      isActive(stored.filterType) ||
      isActive(stored.filterGenre) ||
      isActive(stored.filterStatus) ||
      isActive(stored.filterRating) ||
      isActive(stored.filterRatingLabel) ||
      (Array.isArray(stored.selectedTags) && stored.selectedTags.length > 0);
  } catch (e) {
    console.warn('Could not read stored filters:', e);
  }

  const proceed = !hasFilters || window.confirm(
    'Filters are currently active. Reset filters and search by this tag instead?'
  );

  if (proceed) {
    const params = new URLSearchParams();
    params.set('tag', tag);
    navigate(`/search?${params.toString()}`);
  }
}}


    style={{
      backgroundColor: uiColor === 'dark' ? '#333' : '#e0e0e0',
      color: uiColor === 'dark' ? '#fff' : '#000',
      borderRadius: '12px',
      padding: '4px 10px',
      fontSize: uiSize === 'lg' ? '1rem' : uiSize === 'sm' ? '0.75rem' : '0.85rem',
      display: 'inline-block',
      cursor: 'pointer',
      textDecoration: 'underline'
    }}
  >
    {tag}
  </span>
))}

              </div>
            </div>


            )}

          
          {tune.source && <p><strong>Source:</strong> {tune.source}</p>}
       

          {tune.lyrics && (
                      <details open style={{ marginBottom: '1rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Lyrics</summary>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{tune.lyrics}</div>
          </details>

          )}
          
          {tune.chords && (
            <details style={{ marginBottom: '1rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Chords</summary>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{tune.chords}</div>
            </details>
          )}

          {tune.abc && (
            <>
            <details open style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>ABC Notation</summary>
<div style={{ overflowX: 'auto', width: '100%' }}>
  <div
    style={{
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '12px',
      boxShadow: '0 0 6px rgba(0, 0, 0, 0.1)',
      minWidth: 'min(100%, 320px)', // minimum width safety net
      maxWidth: '100%',
      boxSizing: 'border-box',
    }}
  >
    <AbcNotation abc={tune.abc} />
    <AbcMidiPlayer abc={tune.abc} />

  </div>
</div>





            </details>

              <details style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.9rem' }}>Show Raw ABC Code</summary>
                <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{sanitizeAbcBody(tune.abc)}</pre>
              </details>
            </>
          )}

          {tune.notes && (
            <details style={{ marginBottom: '1rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Notes</summary>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{tune.notes}</div>
            </details>
          )}


{Array.isArray(tune.attachments) && tune.attachments.length > 0 && (
  <div style={{ marginTop: '1.5rem', marginBottom: '1rem', width: '100%' }}>
   <div
  onClick={() => setAttachmentsOpen(!attachmentsOpen)}
  style={{
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    userSelect: 'none'
  }}
>
  <span
    style={{
      display: 'inline-block',
      width: '1em',
      textAlign: 'center',
      transform: attachmentsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }}
  >
    ▶
  </span>
  <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>Attachments</span>
</div>


    {attachmentsOpen && (
      <ul style={{
        listStyle: 'none',
        paddingLeft: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        alignItems: 'center'
      }}>
        {tune.attachments.map((file) => (
          <li key={file.url} style={{ marginBottom: '1rem', textAlign: 'center' }}>
            {file.type.startsWith('image/') ? (
              <>
                <div
                  onClick={() => setFullscreenImgUrl(file.url)}
                  style={{
                    cursor: 'pointer',
                    display: 'inline-block',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    width: '100%',
                    maxWidth: '300px',
                    backgroundColor: '#f8f8f8'
                  }}
                >
                  <img
                    src={file.url}
                    alt={file.name}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
                <div
                  onClick={() => setFullscreenImgUrl(file.url)}
                  style={{
                    marginTop: '6px',
                    fontSize: '0.85rem',
                    color: 'var(--text-link)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    maxWidth: '300px',
                    wordBreak: 'break-word'
                  }}
                >
                  🖼 {file.name}
                </div>
              </>
            ) : file.type === 'application/pdf' ? (
              <>
                <div style={{
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: '300px',
                  height: '400px',
                  backgroundColor: '#f8f8f8'
                }}>
                  <iframe
                    src={`${file.url}#toolbar=0&navpanes=0&scrollbar=1`}
                    title={file.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                  />
                </div>
                <div
                  onClick={() => setFullscreenPdfUrl(file.url)}
                  style={{
                    marginTop: '6px',
                    fontSize: '0.85rem',
                    color: 'var(--text-link)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    maxWidth: '300px',
                    wordBreak: 'break-word'
                  }}
                >
                  📄 {file.name}
                </div>
              </>
            ) : (
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  backgroundColor: '#eee',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  textDecoration: 'underline',
                  color: 'var(--text-primary)'
                }}
              >
                📎 {file.name}
              </a>
            )}
          </li>
        ))}
      </ul>
    )}

    {/* Fullscreen PDF Viewer */}
    {fullscreenPdfUrl && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1000,
          overflow: 'hidden'
        }}
      >
        <button
          onClick={() => setFullscreenPdfUrl(null)}
          style={{
            position: 'absolute',
            bottom: '1.5rem',
            right: '1.5rem',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#fff',
            color: '#000',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            border: 'none',
            cursor: 'pointer',
            zIndex: 1001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
          }}
        >
          ✕
        </button>
        <iframe
          src={fullscreenPdfUrl}
          title="Fullscreen PDF"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: 'white'
          }}
        />
      </div>
    )}

    {/* Fullscreen Image Viewer */}
    {fullscreenImgUrl && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1000,
          overflow: 'hidden'
        }}
      >
        <button
          onClick={() => setFullscreenImgUrl(null)}
          style={{
            position: 'absolute',
            bottom: '1.5rem',
            right: '1.5rem',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#fff',
            color: '#000',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            border: 'none',
            cursor: 'pointer',
            zIndex: 1001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
          }}
        >
          ✕
        </button>
        <img
          src={fullscreenImgUrl}
          alt="Fullscreen View"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            maxWidth: '95%',
            maxHeight: '95%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '8px',
            boxShadow: '0 0 12px rgba(0,0,0,0.5)'
          }}
        />
      </div>
    )}
  </div>
)}




{Array.isArray(tune.sets) && tune.sets.some(s => s.setId) && (
  <div style={{ marginTop: '1.5rem', marginBottom: '1rem', textAlign: 'center' }}>
    <p><strong>{tune.title} is part of the following set(s):</strong></p>
    <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
      {tune.sets
        .filter(s => s.setId)
        .map((s, i) => {
          const formattedDate = new Date(s.createdAt || Date.now()).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short'
          });

          return (
            <li key={i}>
              <span
                style={{
                  color: 'var(--text-link)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  display: 'inline-block',
                  marginBottom: '0.25rem'
                }}
                onClick={() => navigate(`/sets/${s.setId}`)}
              >
                {(s.title && s.title !== 'Untitled Set')
                  ? `${s.title} — ${formattedDate}`
                  : `Untitled Set — ${formattedDate}`}
              </span>
            </li>
          );
        })}
    </ul>
  </div>
)}


{validSessions.length > 0 && (
  <div style={{ marginTop: '1.5rem', marginBottom: '1rem', textAlign: 'center' }}>
    <p><strong>{tune.title} was played at the following session(s):</strong></p>
    <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
      {validSessions.map((s, i) => {
        const formattedDate = new Date(s.startedAt || Date.now()).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short'
        });

        return (
          <li key={i}>
            <span
              style={{
                color: 'var(--text-link)',
                textDecoration: 'underline',
                cursor: 'pointer',
                display: 'inline-block',
                marginBottom: '0.25rem'
              }}
              onClick={() => navigate(`/session/${s.sessionId}`)}
            >
              {(s.location && s.location !== 'Unnamed')
                ? `${s.location} — ${formattedDate}`
                : `Unnamed — ${formattedDate}`}
            </span>
          </li>
        );
      })}
    </ul>
  </div>
)}



          {[tune.link1, tune.link2, tune.link3].some(Boolean) && (
            <>
              <p><strong>Links:</strong></p>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                {[tune.link1, tune.link2, tune.link3].filter(Boolean).map((link, i) => (
                  <a
                    key={i}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      backgroundColor: 'var(--bg-muted)', // Or '#eee' if no var available
                      color: 'var(--text-primary)',
                      textDecoration: 'underline',
                      fontSize: '0.9rem',
                      maxWidth: '90%',
                      wordBreak: 'break-word',
                      textAlign: 'center'
                    }}
                  >
                    {link}
                  </a>
                ))}
              </div>

            </>
          )}

          <h2>Recordings</h2>
{Array.isArray(tune.recordings) && tune.recordings.length > 0 ? (
<ul style={{ listStyleType: 'none', paddingLeft: 0, maxWidth: '300px', margin: '0 auto' }}>
  {tune.recordings.map((rec, i) => {
    const audioUrl = rec.url || (rec.localBlob && URL.createObjectURL(rec.localBlob));

    const handleDelete = async () => {
      const confirm = window.confirm("Delete this recording?");
      if (!confirm) return;

      const remaining = tune.recordings.filter(r => r.id !== rec.id);

      await dexie.tunes.update(tune.id, { recordings: remaining });

      if (rec.storagePath) {
        try {
          const refToDelete = storageRef(storage, rec.storagePath);
          await deleteObject(refToDelete);
        } catch (err) {
          console.warn("Failed to delete from storage:", err);
        }
      }

      try {
        const ref = doc(db, 'users', user.uid, 'tunes', tune.id);
        const safeForFirebase = remaining.map(r => ({
          id: r.id,
          label: r.label,
          duration: r.duration,
          createdAt: r.createdAt,
          url: r.url || null,
          storagePath: r.storagePath || null
        }));
        await updateDoc(ref, { recordings: safeForFirebase });
      } catch (err) {
        console.warn("Failed to update Firestore:", err);
      }

      // Force reload
      setLoadedTune(prev => ({
        ...prev,
        recordings: remaining
      }));
    };

    return (
      <li key={rec.id} style={{ marginBottom: '1rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.9rem', marginBottom: '4px' }}>{rec.label || 'Untitled'}</div>

        {audioUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <AudioPlayerWithSpeed src={audioUrl} />
            <button
              onClick={handleDelete}
              style={{
                marginTop: '6px',
                fontSize: '0.8rem',
                padding: '2px 6px',
                backgroundColor: '#e44',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🗑 Delete
            </button>
          </div>
        ) : (
          <em style={{ marginLeft: '8px', color: 'red' }}>No audio</em>
        )}
      </li>
    );
  })}
</ul>

) : (
  <p style={{ textAlign: 'center', fontStyle: 'italic', color: 'var(--text-muted)' }}>
    No recordings yet.
  </p>
)}

<div style={{ width: '100%', maxWidth: '300px', margin: '1rem auto' }}>
  <TuneRecorder
    tune={currentTune}
    user={user}
    onUpdateRecordings={(recs) => {
      setCurrentTune(prev => ({ ...prev, recordings: recs }));
      setLoadedTune(prev => ({ ...prev, recordings: recs }));
    }}
    inputStyle={{
      fontSize: '1rem',
      padding: '0.5rem',
      border: '1px solid var(--border)',
      borderRadius: '6px',
      boxSizing: 'border-box',
    }}
    buttonStyle={{
      width: '100%',
      fontSize: '0.9rem',
      padding: '0.4rem 0.75rem',
      backgroundColor: '#444',
      color: '#fff',
      border: '1px solid var(--border)',
      borderRadius: '6px',
      boxSizing: 'border-box',
    }}
    containerStyle={{
      textAlign: 'center'
    }}
  />
</div>





   <button
    className="big-button"
    onClick={() => navigate(`/edit/${tune.id || "imported"}`, { state: { tune } })}

        style={{
          marginTop: '10px',
      background: '#ccc',
      color: '#000',
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem'
    }}
  >
    ✏️ Edit Tune
  </button>
          </div>  
        </div>
      </div>
    </PageWrapperAuto>
  );
}
